import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useMemo } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Label } from '@/components/ui/label.tsx'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { MemberSelector } from '@/components/MemberSelector.tsx'
import { UserAvatar } from '@/components/UserAvatar.tsx'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { useQueryProjectMembers } from '@/requests/queries/useQueryProject.ts'

type Props = {
  storyId: string
  value: Member[]
}

export const Owners = ({ storyId, value }: Props) => {
  const { mutate } = useMutateStory(storyId)
  const { members } = useQueryProjectMembers()
  const sortedMembers = useMemo(
    () => members.filter((m) => m.status === 'ACTIVE').sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  )
  const membersMap = new Map(members.map((member) => [member.id, member]))

  return (
    <div className="flex gap-1 w-full items-center">
      <Label htmlFor={`owners-${storyId}`} variant="title">
        Owners
      </Label>
      <ul className="flex items-center gap-1 w-full justify-end">
        {value.length > 0 ? (
          value.map((owner) => {
            const member = membersMap.get(owner.id)
            return (
              <MemberSelector
                key={owner.id}
                currentMember={owner}
                members={sortedMembers}
                selectedMembers={value}
                onSelect={(item) => mutate({ owners: value.map((_owner) => (owner.id === _owner.id ? item : _owner)) })}
                onRemove={(item) => mutate({ owners: value.filter((owner) => owner.id !== item.id) })}
              >
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="w-3.5 hover:bg-transparent after:hover:opacity-0"
                >
                  <UserAvatar member={member} size="xs" />
                </Button>
              </MemberSelector>
            )
          })
        ) : (
          <li className="flex-grow flex justify-end text-contents-primary font-EN-KR-body2-regular">Add</li>
        )}
      </ul>

      <MemberSelector
        members={sortedMembers}
        selectedMembers={value}
        onSelect={(item) => mutate({ owners: [...value, item] })}
      >
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="w-3.5 min-w-3.5 h-3.5 hover:bg-transparent after:hover:opacity-0"
          disabled={members.length === value.length || value.length >= 5}
        >
          <img src="/images/story-add-owner-icon.svg" alt="add owner" />
        </Button>
      </MemberSelector>
    </div>
  )
}

export const TypedEditableOwners = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<Member[], ComponentProps<typeof Owners>, undefined> & RefAttributes<EditableCommands>
>
